/**
 * Template tokens in cell text and choice labels (panels.html §4 "Text"): `{companion.name}`, `{companion.They}`,
 * `{companion.verb:is|are}`, `{companion.pronoun, select, she {…} he {…} other {…}}`, and `{{`/`}}` for literal braces. The engine's
 * sketches also write `{cast.companion.name}`; both read the same role. A port of shared-cs `Text/TextTemplate.cs` (S1-CHR): `TryParse`
 * and `Check` with the same errors, plus segments for highlighting.
 */

/** shared-cs `TextTemplate.Props`. */
export const TEMPLATE_PROPS = [
  'name',
  'they',
  'them',
  'their',
  'theirs',
  'themself',
  'trait',
] as const;

export type TextToken = { role: string; prop: string; branches: Record<string, string> };

export type TemplateSegment =
  | { kind: 'text'; text: string }
  | { kind: 'token'; text: string; role: string; field: string }
  | { kind: 'bad'; text: string; error: string };

function matchingBrace(t: string, open: number): number {
  let depth = 0;
  for (let i = open; i < t.length; i++) {
    if (t[i] === '{') depth++;
    else if (t[i] === '}' && --depth === 0) return i;
  }
  return -1;
}

const sameExceptFirst = (prop: string) => ![...prop.slice(1)].some((c) => c !== c.toLowerCase());

function parseSelect(token: TextToken, rest: string, body: string): [TextToken | null, string] {
  const comma = rest.indexOf(',');
  if (token.prop !== 'pronoun' || comma < 0 || rest.slice(0, comma).trim() !== 'select')
    return [null, `has a token '{${body}}' that isn't {role.pronoun, select, … other {…}}`];
  const s = rest.slice(comma + 1);
  let i = 0;
  for (;;) {
    while (i < s.length && /\s/.test(s[i]!)) i++;
    if (i >= s.length) break;
    const keyStart = i;
    while (i < s.length && s[i] !== '{' && !/\s/.test(s[i]!)) i++;
    const key = s.slice(keyStart, i);
    while (i < s.length && /\s/.test(s[i]!)) i++;
    if (key.length === 0 || i >= s.length || s[i] !== '{')
      return [null, `has a select '{${body}}' with a branch that isn't key {text}`];
    const end = matchingBrace(s, i);
    if (end < 0) return [null, `has a select '{${body}}' with an unclosed branch`];
    token.branches[key] = s.slice(i + 1, end);
    i = end + 1;
  }
  if (!('other' in token.branches))
    return [null, `has a select '{${body}}' with no 'other' branch`];
  return [token, ''];
}

/** `TextTemplate.ParseToken`: the token, or null and the error. */
export function parseToken(body: string): [TextToken | null, string] {
  const comma = body.indexOf(',');
  let head = (comma >= 0 ? body.slice(0, comma) : body).trim();
  if (head.startsWith('cast.')) head = head.slice(5);
  const dot = head.indexOf('.');
  if (dot <= 0 || dot === head.length - 1)
    return [null, `has a token '{${body}}' that isn't {role.property}`];
  const token: TextToken = { role: head.slice(0, dot), prop: head.slice(dot + 1), branches: {} };
  if (comma >= 0) return parseSelect(token, body.slice(comma + 1), body);
  const prop = token.prop;
  if (prop.toLowerCase().startsWith('verb:')) {
    const forms = prop.slice(5);
    const bar = forms.indexOf('|');
    if (bar <= 0 || bar === forms.length - 1 || forms.indexOf('|', bar + 1) >= 0)
      return [null, `has a verb token '{${body}}' that isn't {role.verb:singular|plural}`];
    token.prop = prop.slice(0, 4);
    return [token, ''];
  }
  if (!(TEMPLATE_PROPS as readonly string[]).includes(prop.toLowerCase()) || !sameExceptFirst(prop))
    return [null, `has an unknown property '${prop}' in '{${body}}'`];
  return [token, ''];
}

/** `TextTemplate.TryParse`: every token (select branches too), or the first error. */
export function tryParse(template: string | null | undefined, tokens: TextToken[]): string {
  const t = template ?? '';
  let i = 0;
  while (i < t.length) {
    const c = t[i]!;
    if ((c === '{' || c === '}') && t[i + 1] === c) {
      i += 2;
      continue;
    }
    if (c === '}') return "has a '}' with no '{'";
    if (c !== '{') {
      i++;
      continue;
    }
    const end = matchingBrace(t, i);
    if (end < 0) return "has a '{' with no '}'";
    const [token, error] = parseToken(t.slice(i + 1, end));
    if (!token) return error;
    tokens.push(token);
    for (const branch of Object.values(token.branches)) {
      const e = tryParse(branch, tokens);
      if (e) return e;
    }
    i = end + 1;
  }
  return '';
}

/** `TextTemplate.Check`: the parse error, or each unknown role once ("where names role 'x', which isn't in the cast."). */
export function checkTemplate(
  template: string | null | undefined,
  roleKnown: (role: string) => boolean,
  where: string,
): string[] {
  const tokens: TextToken[] = [];
  const error = tryParse(template, tokens);
  if (error) return [`${where} ${error}.`];
  const out: string[] = [];
  const reported = new Set<string>();
  for (const token of tokens)
    if (!roleKnown(token.role) && !reported.has(token.role)) {
      reported.add(token.role);
      out.push(`${where} names role '${token.role}', which isn't in the cast.`);
    }
  return out;
}

/** Splits text into plain runs and tokens for highlighting; a token that doesn't parse is a bad segment with the parser's error. */
export function parseTemplate(text: string): TemplateSegment[] {
  const out: TemplateSegment[] = [];
  let plain = '';
  let i = 0;
  const flush = () => {
    if (plain) out.push({ kind: 'text', text: plain });
    plain = '';
  };
  while (i < text.length) {
    const ch = text[i]!;
    if ((ch === '{' || ch === '}') && text[i + 1] === ch) {
      plain += ch;
      i += 2;
      continue;
    }
    if (ch === '}') {
      flush();
      out.push({ kind: 'bad', text: '}', error: "has a '}' with no '{'" });
      i++;
      continue;
    }
    if (ch !== '{') {
      plain += ch;
      i++;
      continue;
    }
    flush();
    const end = matchingBrace(text, i);
    if (end < 0) {
      out.push({ kind: 'bad', text: text.slice(i), error: "has a '{' with no '}'" });
      return out;
    }
    const raw = text.slice(i, end + 1);
    const [token, error] = parseToken(raw.slice(1, -1));
    out.push(
      token
        ? { kind: 'token', text: raw, role: token.role, field: token.prop }
        : { kind: 'bad', text: raw, error },
    );
    i = end + 1;
  }
  flush();
  return out;
}

/** Roles every chain has without casting them. */
export const IMPLICIT_ROLES = ['player', 'leader'] as const;

/**
 * `PanelCellContext.RoleKnown` (S1-CHR): `player`, a cast role, `member:1..6`, `npc:<known npc>` or `zombie:<known zombie>`. With no
 * npc/zombie sets the prefixed forms aren't checked.
 */
export function roleKnown(
  role: string,
  cast: Set<string>,
  npcs?: Set<string>,
  zombies?: Set<string>,
): boolean {
  if (!role) return false;
  if (role === 'player' || cast.has(role)) return true;
  if (role.startsWith('member:')) {
    const n = role.slice(7);
    return /^[1-6]$/.test(n);
  }
  if (role.startsWith('npc:')) return !npcs || npcs.has(role.slice(4));
  if (role.startsWith('zombie:')) return !zombies || zombies.has(role.slice(7));
  return false;
}
